export type ScheduleType = "as_needed" | "daily" | "weekly" | "monthly" | "quarterly";

export type PlatformRow = {
  /** Stable list identity for React keys (Ads Assistant / editors); omit when persisting to JSON. */
  rowId?: string;
  key: string;
  label: string;
  daily_slots: number;
};

export type ManualRow = {
  id: string;
  label: string;
  description: string;
  requires_confirm: boolean;
  schedule_type: ScheduleType;
  weekdays: boolean[];
  month_days: string;
};

export const ID_RE = /^[a-z][a-z0-9_]{0,62}$/;
export const PLATFORM_KEY_RE = /^[a-z][a-z0-9_]{0,32}$/;
export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const DEFAULT_PLATFORMS: PlatformRow[] = [
  { key: "facebook", label: "Facebook Business Page", daily_slots: 3 },
  { key: "google", label: "Google Business", daily_slots: 3 },
];

export const DEFAULT_MANUAL: ManualRow[] = [
  { id: "office_tidy", label: "Tidy / clean your office", description: "Quick reset so the space is ready for customers and the team.", requires_confirm: false, schedule_type: "daily", weekdays: [true, true, true, true, true, true, true], month_days: "" },
  { id: "bathroom_clean", label: "Clean restroom / bathroom", description: "", requires_confirm: false, schedule_type: "daily", weekdays: [true, true, true, true, true, true, true], month_days: "" },
  { id: "notifications_inbox", label: "Clear notifications that need you", description: "Inbox, SMS, app alerts — anything waiting for a yes/no or follow-up.", requires_confirm: false, schedule_type: "daily", weekdays: [true, true, true, true, true, true, true], month_days: "" },
  { id: "location_walkthrough", label: "Walk the location", description: "Trash pickup, inventory staging, obvious repairs or safety issues.", requires_confirm: false, schedule_type: "daily", weekdays: [true, true, true, true, true, true, true], month_days: "" },
];

export function cloneDefaultPlatforms() {
  return DEFAULT_PLATFORMS.map((r) => ({ ...r }));
}

/** Hydrate platform rows from checklist `ads` with stable `rowId` for list keys (not persisted). */
export function platformRowsWithStableIdsFromAds(ads: Record<string, unknown> | null | undefined): PlatformRow[] {
  const entries = ads && Array.isArray(ads.platform_entries) ? (ads.platform_entries as Record<string, unknown>[]) : [];
  if (entries.length > 0) {
    return entries
      .map((e) => ({
        rowId: crypto.randomUUID(),
        key: typeof e.key === "string" ? e.key : "",
        label: typeof e.label === "string" ? e.label : "",
        daily_slots: Math.min(20, Math.max(1, parseInt(String(e.daily_slots ?? 1), 10) || 1)),
      }))
      .filter((e) => PLATFORM_KEY_RE.test(e.key));
  }
  const t = ads?.target != null ? Math.min(20, Math.max(1, parseInt(String(ads.target), 10) || 6)) : 6;
  const half = Math.max(1, Math.floor(t / 2));
  return [
    { rowId: crypto.randomUUID(), key: "facebook", label: "Facebook Business Page", daily_slots: half },
    { rowId: crypto.randomUUID(), key: "google", label: "Google Business", daily_slots: t - half },
  ];
}

export function cloneDefaultManualRows() {
  return DEFAULT_MANUAL.map((r) => ({ ...r, weekdays: [...r.weekdays] }));
}

export function parseMonthDaysStr(s: string): number[] {
  const t = s.trim();
  if (!t) return [];
  return [...new Set(t.split(/[\s,]+/).map((x) => parseInt(x, 10)).filter((n) => n >= 1 && n <= 31))];
}

export function parseDayList(s: string): number[] {
  const t = s.trim();
  if (!t) return [];
  return [...new Set(t.split(/[\s,]+/).map((x) => parseInt(x, 10)).filter((n) => n >= 1 && n <= 365))];
}

export function scheduleFromRow(row: ManualRow): Record<string, unknown> {
  if (row.schedule_type === "as_needed") return { type: "as_needed" };
  if (row.schedule_type === "daily") return { type: "daily" };
  if (row.schedule_type === "weekly") return { type: "weekly", weekdays: row.weekdays.map((on, i) => (on ? i : -1)).filter((i) => i >= 0) };
  if (row.schedule_type === "monthly") {
    const md = parseMonthDaysStr(row.month_days);
    return { type: "monthly", month_days: md.length ? md : [1] };
  }
  if (row.schedule_type === "quarterly") {
    const md = parseMonthDaysStr(row.month_days);
    return { type: "quarterly", month_days: md.length ? md : [1] };
  }
  return { type: "daily" };
}

export function manualRowFromConfig(m: Record<string, unknown>): ManualRow | null {
  if (typeof m.id !== "string" || typeof m.label !== "string") return null;
  const sch = m.schedule as Record<string, unknown> | undefined;
  let schedule_type: ScheduleType = "daily";
  const weekdays = [false, false, false, false, false, false, false];
  let month_days = Array.isArray(m.month_days) ? (m.month_days as number[]).join(", ") : "";
  if (sch && typeof sch === "object" && typeof sch.type === "string") {
    const st = sch.type as string;
    if (st === "as_needed" || st === "daily" || st === "weekly" || st === "monthly" || st === "quarterly") schedule_type = st;
    if (st === "weekly" && Array.isArray(sch.weekdays)) for (const n of sch.weekdays as number[]) if (typeof n === "number" && n >= 0 && n <= 6) weekdays[n] = true;
    if ((st === "monthly" || st === "quarterly") && Array.isArray(sch.month_days)) month_days = (sch.month_days as number[]).join(", ");
  } else if (Array.isArray(m.month_days) && (m.month_days as number[]).length) {
    schedule_type = "monthly";
    month_days = (m.month_days as number[]).join(", ");
  }
  if (schedule_type === "weekly" && !weekdays.some(Boolean)) weekdays.fill(true);
  return { id: m.id, label: m.label, description: typeof m.description === "string" ? m.description : "", requires_confirm: m.requires_confirm === true, schedule_type, weekdays, month_days };
}

